<script setup lang="ts">
import { defineEmits, defineProps, ref } from "vue";
import { useI18n } from "vue-i18n";
import CancelIcon from "@/components/icons/CancelIcon.vue";
import CustomSlider from "@/components/inputs/CustomSlider.vue";
import CheckBox from "@/components/inputs/CheckBox.vue";
import ArrowIcon from "@/components/icons/ArrowIcon.vue";
import { onlyDigits } from "@/utils/validators";
import PrintButton from "@/components/buttons/PrintButton.vue";
import AppSelect from "@/components/inputs/AppSelect.vue";
import MonthShaIcon from "@/components/icons/apartmentPlan/MonthShaIcon.vue";
import YearShaIcon from "@/components/icons/apartmentPlan/YearShaIcon.vue";
import TaiShooIcon from "@/components/icons/apartmentPlan/TaiShooIcon.vue";
import SooiPoIcon from "@/components/icons/apartmentPlan/SooiPoIcon.vue";
import {
  APARTMENT_MONTHS_LIST,
  APARTMENT_MONTHS_VALUES,
  IApartmentMonth,
  MonthKey,
  ToggleKey,
} from "@/constants/apartmentPlan";
import InfoSquareIcon from "@/components/icons/apartmentPlan/InfoSquareIcon.vue";
import AppTooltip from "@/components/AppTooltip.vue";
import ModalComponent from "@/components/ModalComponent.vue";
import VideoInstructionButton from "@/views/Calculators/ApartmentPlan/components/VideoInstructionButton.vue";

const props = defineProps<{
  hasDiagramDate: boolean;
  imageName: string | null;
  scale: number;
  diagramScale: number;
  azm: number;
  facadeDirection: number | null;
  addPointMode: boolean;
  movePointMode: boolean;
  removePointMode: boolean;
  toggles: Record<ToggleKey, boolean>;
}>();

const emit = defineEmits<{
  (e: "select-image"): void;
  (e: "clear-image"): void;
  (e: "update:scale", scale: number): void;
  (e: "update:diagram-scale", scale: number): void;
  (e: "update:azm", azmAngle: number): void;
  (e: "update:buildYear", buildYear: number): void;
  (e: "update:starsYear", starsYear: number): void;
  (e: "update:buildMonth", buildMonth: IApartmentMonth): void;
  (e: "changeFacadeDirection", directionDeg: number): void;
  (e: "toggle:move-image"): void;
  (e: "toggle:add-point"): void;
  (e: "toggle:move-point"): void;
  (e: "toggle:remove-point"): void;
  (e: "toggle:lo-shoo"): void;
  (e: "toggle:sectors-diagram"): void;
  (e: "toggle:middle-12-mountains"): void;
  (e: "toggle:border-12-mountains"): void;
  (e: "toggle:move-mountains-labels"): void;
  (e: "export-as-png"): void;
  (e: "export-as-pdf"): void;
  (e: "print"): void;
  (e: "toggle", key: ToggleKey): void;
}>();

const { t } = useI18n();

const facadeDirections = [
  { angle: 90, iconClass: "rotate-45" },
  { angle: 180, iconClass: "rotate-[135deg]" },
  { angle: 0, iconClass: "-rotate-45" },
  { angle: 270, iconClass: "-rotate-[135deg]" },
];

const showInfoModal = ref<boolean>(false);
const modalType = ref<"noble-stars" | "current-stars">();

function blockInvalidKeys(e: KeyboardEvent) {
  const invalid = ["e", "E", "+"];
  if (invalid.includes(e.key)) e.preventDefault();
}

function validateBuildYear(e: KeyboardEvent) {
  if (!onlyDigits(e.key, e.ctrlKey)) {
    e.preventDefault();
  }
}

function updateAzmValue(e: Event) {
  const target = e.target as HTMLInputElement;
  let value = Number(target.value);
  if (value < -360) {
    value = -360;
  } else if (value > 360) {
    value = 360;
  }
  emit("update:azm", value);
}

function updateBuildYearValue(e: Event) {
  const buildYear = getYearFromEvent(e);
  if (buildYear !== null) {
    emit("update:buildYear", buildYear);
  }
}

function updateStarsYearValue(e: Event) {
  const starsYear = getYearFromEvent(e);
  if (starsYear !== null) {
    emit("update:starsYear", starsYear);
  }
}

function getYearFromEvent(e: Event) {
  const target = e.target as HTMLInputElement;
  if (target.value.length < 4) {
    return null;
  }
  return Number(target.value);
}

function openInfoModal(openModalType: "noble-stars" | "current-stars") {
  modalType.value = openModalType;
  showInfoModal.value = true;
}
</script>

<template>
  <div
    class="flex flex-col gap-6 items-start pl-4 py-4 xl:py-6 min-w-[233px] max-w-[233px] bg-white border-l border-ternary overflow-y-auto xl:max-h-[calc(100vh-64px)] max-h-[calc(100vh-56px)] select-none"
  >
    <VideoInstructionButton />
    <!-- SELECT IMAGE OR CLEAR IMAGE -->
    <template v-if="imageName">
      <div class="w-full flex items-center justify-between gap-2 min-h-9">
        <span class="text-black text-sm text-left break-all">
          {{ imageName }}
        </span>
        <button
          class="w-6 h-6 rounded-full border border-black flex items-center justify-center flex-shrink-0"
          @click="emit('clear-image')"
        >
          <CancelIcon class="text-black" />
        </button>
      </div>
    </template>
    <template v-else>
      <button
        class="shrink-0 h-9 w-full rounded-[100px] border border-brown text-sm text-brown"
        @click="emit('select-image')"
      >
        {{ t("calculators.apartment-plan.actions.file-select") }}
      </button>
    </template>
    <!-- SCALE RANGE -->
    <div class="flex flex-col gap-2 w-full">
      <p class="text-brown text-sm text-left">
        {{ t("calculators.apartment-plan.actions.scale") }}
      </p>
      <CustomSlider
        :model-value="props.scale"
        :min="0.1"
        :max="3"
        :step="0.01"
        @update:modelValue="(val: number) => emit('update:scale', val)"
      />
    </div>
    <!-- AZM FACADE -->
    <div class="flex flex-col gap-3">
      <!-- FACADE ANGLE -->
      <div class="flex items-center justify-between gap-1">
        <span class="text-brown text-sm">
          {{ t("calculators.apartment-plan.actions.azm-facade") }}
        </span>
        <input
          id="azm"
          type="number"
          min="-360"
          max="360"
          class="num-input"
          :value="props.azm"
          @keydown="blockInvalidKeys"
          @input="updateAzmValue"
        />
      </div>
      <!-- BUILD YEAR -->
      <div class="flex items-center justify-between gap-1">
        <span class="text-brown text-sm text-left">
          {{ t("calculators.apartment-plan.actions.build-year") }}
        </span>
        <input
          id="buildYear"
          type="text"
          class="num-input"
          maxlength="4"
          autocomplete="off"
          @keydown="validateBuildYear"
          @input="updateBuildYearValue"
        />
      </div>
      <!-- STARS YEAR -->
      <div class="flex items-center justify-between gap-1">
        <span class="text-brown text-sm">
          {{ t("calculators.apartment-plan.actions.stars-year") }}
        </span>
        <input
          id="tarsYear"
          type="text"
          class="num-input"
          maxlength="4"
          autocomplete="off"
          @keydown="validateBuildYear"
          @input="updateStarsYearValue"
        />
      </div>
      <!-- BUILD MONTH -->
      <div class="flex flex-col gap-1">
        <p class="text-brown text-sm text-left">Месяц постройки:</p>
        <AppSelect
          :items="APARTMENT_MONTHS_LIST"
          :default-value="APARTMENT_MONTHS_LIST[0]"
          @select-option="(selectedMonth: MonthKey) => emit('update:buildMonth', APARTMENT_MONTHS_VALUES[selectedMonth])"
        >
          <template #option="{ item }">
            {{ t(`months.${item}`) }}
          </template>
        </AppSelect>
      </div>
      <!-- FACADE DIRECTION -->
      <div class="grid grid-cols-2 gap-1">
        <p class="col-span-2 text-brown text-sm text-left">
          Направление фасада:
        </p>
        <button
          v-for="facadeDirection in facadeDirections"
          :key="facadeDirection.angle"
          class="facade-direction-button"
          :class="
            facadeDirection.angle === props.facadeDirection
              ? 'pointer-events-none facade-direction-button-selected'
              : 'facade-direction-button-active'
          "
          @click="emit('changeFacadeDirection', facadeDirection.angle)"
        >
          <ArrowIcon class="text-white" :class="facadeDirection.iconClass" />
        </button>
      </div>
    </div>
    <!-- MOVE IMAGE TOGGLE -->
    <CheckBox
      input-id="move-image"
      :label="t('calculators.apartment-plan.actions.move-image')"
      @toggle="emit('toggle:move-image')"
    />
    <!-- POINTS BLOCK -->
    <div class="flex flex-col gap-2 items-start">
      <p class="font-bold text-base leading-[20px] text-black">
        {{ t("calculators.apartment-plan.actions.points-group") }}
      </p>
      <CheckBox
        input-id="add-point"
        :label="t('calculators.apartment-plan.actions.add-point')"
        :isEnabled="props.addPointMode"
        @toggle="emit('toggle:add-point')"
      />
      <CheckBox
        input-id="move-point"
        :label="t('calculators.apartment-plan.actions.move-point')"
        :isEnabled="props.movePointMode"
        @toggle="emit('toggle:move-point')"
      />
      <CheckBox
        input-id="move-point"
        :label="t('calculators.apartment-plan.actions.remove-point')"
        :isEnabled="props.removePointMode"
        @toggle="emit('toggle:remove-point')"
      />
    </div>
    <!-- NETS BLOCK -->
    <div class="flex flex-col gap-2 items-start">
      <p class="font-bold text-base leading-[20px] text-black">
        {{ t("calculators.apartment-plan.actions.nets-group") }}
      </p>
      <CheckBox
        input-id="lo-shoo"
        :label="t('calculators.apartment-plan.actions.lo-shoo')"
        @toggle="emit('toggle', 'lo-shoo')"
      />
      <CheckBox
        input-id="sectors-diagram"
        :label="t('calculators.apartment-plan.actions.sectors-diagram')"
        :isEnabled="toggles['sectors-diagram']"
        @toggle="emit('toggle', 'sectors-diagram')"
      />
      <CheckBox
        class="pl-5"
        :isDisabled="!hasDiagramDate"
        :isEnabled="toggles['tai-shoo']"
        input-id="tai-shoo"
        :label="t('calculators.apartment-plan.actions.tai-shoo')"
        @toggle="emit('toggle', 'tai-shoo')"
      >
        <template #label="{ label }">
          <TaiShooIcon class="ml-2" />
          <span class="block text-left font-normal text-sm text-brown ml-2">
            {{ label }}
          </span>
        </template>
      </CheckBox>
      <CheckBox
        class="pl-5"
        input-id="sui-po"
        :isDisabled="!hasDiagramDate"
        :isEnabled="toggles['sui-po']"
        :label="t('calculators.apartment-plan.actions.sui-po')"
        @toggle="emit('toggle', 'sui-po')"
      >
        <template #label="{ label }">
          <SooiPoIcon class="ml-2" />
          <p class="text-left font-normal text-sm text-brown ml-2">
            {{ label }}
          </p>
        </template>
      </CheckBox>
      <CheckBox
        class="pl-5"
        input-id="year-sha"
        :isDisabled="!hasDiagramDate"
        :isEnabled="toggles['year-sha']"
        :label="t('calculators.apartment-plan.actions.year-sha')"
        @toggle="emit('toggle', 'year-sha')"
      >
        <template #label="{ label }">
          <YearShaIcon class="ml-2" />
          <p class="text-left font-normal text-sm text-brown ml-2">
            {{ label }}
          </p>
        </template>
      </CheckBox>
      <CheckBox
        class="pl-5"
        input-id="month-sha"
        :isDisabled="!hasDiagramDate"
        :isEnabled="toggles['month-sha']"
        :label="t('calculators.apartment-plan.actions.month-sha')"
        @toggle="emit('toggle', 'month-sha')"
      >
        <template #label="{ label }">
          <MonthShaIcon class="ml-2" />
          <p class="text-left font-normal text-sm text-brown ml-2">
            {{ label }}
          </p>
          <AppTooltip :disabled="!hasDiagramDate">
            <template #trigger>
              <InfoSquareIcon class="ml-auto !size-5" />
            </template>
            <template #popper>
              <p class="text-center whitespace-pre-line">
                {{ t("calculators.apartment-plan.info.month-sha") }}
              </p>
            </template>
          </AppTooltip>
        </template>
      </CheckBox>
      <CheckBox
        class="pl-5"
        input-id="noble-stars"
        :isDisabled="!hasDiagramDate"
        :isEnabled="toggles['noble-stars']"
        :label="t('calculators.apartment-plan.actions.noble-stars')"
        @toggle="emit('toggle', 'noble-stars')"
      >
        <template #label="{ label }">
          <p class="text-left font-normal text-sm text-brown ml-2 w-[120px]">
            {{ label }}
          </p>
          <InfoSquareIcon
            class="ml-auto !size-5"
            :class="{ 'cursor-pointer': hasDiagramDate }"
            @click.prevent="openInfoModal('noble-stars')"
          />
        </template>
      </CheckBox>
      <CheckBox
        class="pl-5"
        input-id="current-stars"
        :isDisabled="!hasDiagramDate"
        :isEnabled="toggles['current-stars']"
        :label="t('calculators.apartment-plan.actions.current-stars')"
        @toggle="emit('toggle', 'current-stars')"
      >
        <template #label="{ label }">
          <p class="text-left font-normal text-sm text-brown ml-2 w-[120px]">
            {{ label }}
          </p>
          <InfoSquareIcon
            class="ml-auto !size-5"
            :class="{ 'cursor-pointer': hasDiagramDate }"
            @click.prevent="openInfoModal('current-stars')"
          />
        </template>
      </CheckBox>
      <div class="w-full">
        <p class="text-brown text-sm text-left">
          {{ t("calculators.apartment-plan.actions.diagram-scale") }}
        </p>
        <CustomSlider
          :model-value="props.diagramScale"
          :min="0.1"
          :max="3"
          :step="0.01"
          @update:modelValue="(val: number) => emit('update:diagram-scale', val)"
        />
      </div>
    </div>
    <!-- MOUNTAINS BLOCK -->
    <div class="flex flex-col gap-2 items-start">
      <p class="font-bold text-base leading-[20px] text-black">
        {{ t("calculators.apartment-plan.actions.mountains-group") }}
      </p>
      <CheckBox
        input-id="middle-12-mountains"
        :label="t('calculators.apartment-plan.actions.middle-12-mountains')"
        @toggle="emit('toggle', 'middle-12-mountains')"
      />
      <CheckBox
        input-id="border-12-mountains"
        :label="t('calculators.apartment-plan.actions.border-12-mountains')"
        @toggle="emit('toggle', 'border-12-mountains')"
      />
      <CheckBox
        input-id="move-mountains-labels"
        :label="t('calculators.apartment-plan.actions.move-mountains-labels')"
        @toggle="emit('toggle', 'move-mountains-labels')"
      />
    </div>
    <!-- EXPORT BLOCK -->
    <div class="w-full flex gap-2 justify-between">
      <div class="flex gap-2">
        <button
          class="w-[62px] h-9 rounded-[100px] border border-brown text-sm text-brown"
          @click="emit('export-as-png')"
        >
          PNG
        </button>
        <button
          class="w-[62px] h-9 rounded-[100px] border border-brown text-sm text-brown"
          @click="emit('export-as-pdf')"
        >
          PDF
        </button>
      </div>
      <PrintButton btn-classes="size-9" @click="emit('print')" />
    </div>
    <ModalComponent :show="showInfoModal" @close="showInfoModal = false">
      <template #header>
        <p class="font-semibold text-2xl text-black">
          {{ t(`calculators.apartment-plan.actions.${modalType}`) }}
        </p>
      </template>
      <template #content>
        <p
          class="text-left text-base leading-[20px] text-brown whitespace-pre-line"
        >
          {{ t(`calculators.apartment-plan.info.${modalType}`) }}
        </p>
      </template>
    </ModalComponent>
  </div>
</template>

<style scoped>
.facade-direction-button {
  @apply rounded-md h-10 flex items-center justify-center;
}
.facade-direction-button-active {
  @apply bg-brown-gradient hover:bg-orange-gradient active:opacity-80 cursor-pointer;
}
.facade-direction-button-selected {
  @apply bg-orange-gradient;
}

.num-input {
  @apply rounded-lg border border-muted text-sm text-black focus:outline-none h-7 w-[87px] px-2 number-input-no-spin;
}
</style>
